import React, { useState, useEffect } from 'react';
import { getLogisticsRecords, getRMARecords } from '../utils/supabase';
import './RecordsViewer.css';

const RecordsViewer = ({ activeTab = 'logistics' }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    loadRecords();
  }, [activeTab]);

  const loadRecords = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let data;
      if (activeTab === 'logistics') {
        data = await getLogisticsRecords(50);
      } else if (activeTab === 'rma') {
        data = await getRMARecords(50);
      }
      setRecords(data || []);
    } catch (err) {
      console.error('Error loading records:', err);
      setError(err.message || 'Error al cargar los registros');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleImageClick = (imageUrl) => {
    setImagePreview(imageUrl);
  };

  const closeImagePreview = () => {
    setImagePreview(null);
  };

  const renderLogisticsRecord = (record) => (
    <div key={record.id} className="record-card">
      <div className="record-header">
        <span className="record-date">{formatDate(record.created_at)}</span>
        <span className="record-sku">SKU: {record.sku}</span>
      </div>
      
      <div className="record-content">
        {record.serial_numbers && record.serial_numbers.length > 0 && (
          <div className="serial-numbers">
            <strong>Números de Serie:</strong>
            <ul>
              {record.serial_numbers.map((serial, idx) => (
                <li key={idx}>{serial}</li>
              ))}
            </ul>
          </div>
        )}
        
        {record.images && record.images.length > 0 && (
          <div className="record-images">
            <strong>Imágenes ({record.images.length}):</strong>
            <div className="image-thumbnails">
              {record.images.map((imageUrl, idx) => (
                <img
                  key={idx}
                  src={imageUrl}
                  alt={`Imagen ${idx + 1}`}
                  className="image-thumbnail"
                  onClick={() => handleImageClick(imageUrl)}
                  onError={(e) => {
                    console.error('Error loading image:', imageUrl);
                    e.target.style.display = 'none';
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderRMARecord = (record) => (
    <div key={record.id} className="record-card">
      <div className="record-header">
        <span className="record-date">{formatDate(record.created_at)}</span>
        <span className="record-rma">RMA: {record.rma_number}</span>
      </div>
      
      <div className="record-content">
        <div className="record-details">
          {record.pallet_number && (
            <p><strong>Pallet:</strong> {record.pallet_number}</p>
          )}
          <p><strong>Embalaje:</strong> {record.packaging_type}</p>
          <p><strong>Condición:</strong> {record.condition}</p>
          
          {record.dimensions && (
            <p><strong>Dimensiones:</strong> {record.dimensions.length} x {record.dimensions.width} x {record.dimensions.height} cm</p>
          )}
          
          {record.weight && (
            <p><strong>Peso:</strong> {record.weight} kg</p>
          )}
          
          {record.notes && (
            <p><strong>Notas:</strong> {record.notes}</p>
          )}
        </div>
        
        {record.images && record.images.length > 0 && (
          <div className="record-images">
            <strong>Imágenes ({record.images.length}):</strong>
            <div className="image-thumbnails">
              {record.images.map((imageUrl, idx) => (
                <img
                  key={idx}
                  src={imageUrl}
                  alt={`Imagen ${idx + 1}`}
                  className="image-thumbnail"
                  onClick={() => handleImageClick(imageUrl)}
                  onError={(e) => {
                    console.error('Error loading image:', imageUrl);
                    e.target.style.display = 'none';
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="records-viewer">
        <div className="loading">Cargando registros...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="records-viewer">
        <div className="error-message">
          ❌ Error: {error}
          <button onClick={loadRecords} className="retry-button">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="records-viewer">
      <div className="records-header">
        <h2>
          {activeTab === 'logistics' ? 'Registros de Logística' : 'Registros RMA'}
        </h2>
        <button onClick={loadRecords} className="refresh-button">
          🔄 Actualizar
        </button>
      </div>

      {records.length === 0 ? (
        <div className="no-records">
          <p>No hay registros guardados aún.</p>
        </div>
      ) : (
        <div className="records-list">
          {records.map(record => 
            activeTab === 'logistics' 
              ? renderLogisticsRecord(record)
              : renderRMARecord(record)
          )}
        </div>
      )}

      {imagePreview && (
        <div className="image-preview-overlay" onClick={closeImagePreview}>
          <div className="image-preview-container">
            <img src={imagePreview} alt="Vista previa" />
            <button className="close-preview" onClick={closeImagePreview}>
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordsViewer;